// Reports refused account creations to account_creation_log
// (20261012190000) so the office can see who tried to sign up and why it
// didn't work. Successes are logged by a trigger on auth.users, not from here.
//
// A refused sign-up has no session and often a flaky connection, so reports
// queue in localStorage first and flush on a later load, the same way
// clientDiagnostics does. Best-effort everywhere; never send the password.
import { supabase } from "@/integrations/supabase/client";
import { getAppVersionInfo } from "@/hooks/use-app-version-tracker";

const QUEUE_KEY = "cha-account-creation-refusals";
const MAX_QUEUED = 10;

export type AccountCreationSource = "self_signup" | "admin";

interface RefusalRow {
  p_source: AccountCreationSource;
  p_email: string;
  p_reason_code: string;
  p_reason: string;
  p_details: Record<string, unknown>;
  p_user_agent: string | null;
}

/**
 * Sort a refusal into a short code the admin screen can count and explain.
 * Works on the auth server's error code when present, its message otherwise.
 */
export function classifyAccountCreationError(
  message: string | null | undefined,
  code?: string | null,
  status?: number | null,
): string {
  const msg = message ?? "";
  if (/pwned|known to be weak|easy to guess|breach|leaked/i.test(msg)) return "password_breached";
  if (/at least one character of each/i.test(msg)) return "password_characters";
  if (/password/i.test(msg) && /at least \d+ characters/i.test(msg)) return "password_too_short";
  if (code === "weak_password" || (/password/i.test(msg) && /weak/i.test(msg))) return "password_weak";
  if (code === "user_already_exists" || code === "email_exists" || /already (been )?registered|already exists/i.test(msg)) return "already_registered";
  if (code === "email_address_invalid" || /invalid email|email address .* invalid|valid email/i.test(msg)) return "invalid_email";
  if (code === "signup_disabled" || /signups? not allowed|signup.*disabled/i.test(msg)) return "signup_disabled";
  if (status === 429 || /rate limit|too many requests/i.test(msg)) return "rate_limited";
  if (/database error saving new user/i.test(msg)) return "database_error";
  if (/failed to fetch|load failed|network/i.test(msg)) return "network";
  if (typeof status === "number" && status >= 500) return "server_error";
  return "unknown";
}

const readQueue = (): RefusalRow[] => {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeQueue = (rows: RefusalRow[]): void => {
  try {
    if (rows.length === 0) localStorage.removeItem(QUEUE_KEY);
    else localStorage.setItem(QUEUE_KEY, JSON.stringify(rows.slice(-MAX_QUEUED)));
  } catch {
    // Storage full or blocked - drop silently.
  }
};

// log_account_creation_refusal isn't in the generated Supabase types until
// Lovable regenerates them - call .rpc as a method so `this` stays bound.
const sendOne = async (row: RefusalRow): Promise<boolean> => {
  try {
    const client = supabase as unknown as {
      rpc: (fn: string, args: RefusalRow) => PromiseLike<{ error: { message: string } | null }>;
    };
    const { error } = await client.rpc("log_account_creation_refusal", row);
    return !error;
  } catch {
    return false;
  }
};

// One send at a time, so a quick second refusal can't deliver the first twice.
let sending = false;

async function sendQueue(): Promise<void> {
  if (sending) return;
  sending = true;
  try {
    let pending = readQueue();
    while (pending.length > 0) {
      const unsent: RefusalRow[] = [];
      for (const row of pending) {
        if (!(await sendOne(row))) unsent.push(row);
      }
      // Rows queued while we were sending stay queued for the next pass.
      const added = readQueue().slice(pending.length);
      writeQueue([...unsent, ...added]);
      if (unsent.length > 0) return; // offline - try again on a later load
      pending = added;
    }
  } finally {
    sending = false;
  }
}

export function reportAccountCreationRefusal(input: {
  source: AccountCreationSource;
  email: string;
  reasonCode: string;
  reason: string;
  details?: Record<string, unknown>;
}): void {
  const row: RefusalRow = {
    p_source: input.source,
    p_email: input.email.trim().slice(0, 255),
    p_reason_code: input.reasonCode,
    p_reason: input.reason.slice(0, 500),
    p_details: {
      ...input.details,
      release: getAppVersionInfo().commitHash,
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
      queued_at: new Date().toISOString(),
    },
    p_user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : null,
  };
  writeQueue([...readQueue(), row]);
  void sendQueue();
}

/** Deliver refusals an earlier visit couldn't. Called once at startup. */
export function flushAccountCreationRefusals(): void {
  if (typeof window === "undefined") return;
  window.setTimeout(() => void sendQueue(), 8000);
}
